import React from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, Crown, ExternalLink, Loader2 } from "lucide-react";
import { useSubscription } from "../hooks/useSubscription";
import "./ManageSubscription.css";

const ManageSubscription: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const {
    hasActiveSubscription,
    subscription,
    status,
    isLoading,
    subscribe,
    manageSubscription,
    isCreatingCheckout,
    isCreatingPortal,
    error,
  } = useSubscription();

  const handleSubscribe = () => {
    subscribe({
      successUrl: `${window.location.origin}/profile?from_stripe=true`,
      cancelUrl: `${window.location.origin}/profile?subscription=cancelled`,
    });
  };

  const handleManageSubscription = () => {
    manageSubscription({
      returnUrl: `${window.location.origin}/profile`,
    });
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3>
            <CreditCard size={16} /> {t("common:subscription.title")}
          </h3>
        </div>
        <div className="subscription-loading">
          <Loader2 size={16} className="loading-spinner" />
          <span>{t("common:subscription.checkingStatus")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>
          <CreditCard size={16} /> {t("common:subscription.title")}
        </h3>
      </div>

      {error && (
        <div className="subscription-error">
          <strong>{t("common:status.error")}:</strong> {error}
        </div>
      )}

      <div className="subscription-content">
        {hasActiveSubscription ? (
          <div className="subscription-active">
            <div className="subscription-status">
              <div className="status-icon">
                <Crown size={16} />
              </div>
              <div className="status-info">
                <h4>{t("common:subscription.premiumPlanActive")}</h4>
                <p>
                  {t("common:subscription.statusLabel")}:{" "}
                  <span className={`status-badge status-${status}`}>
                    {status || t("common:status.active").toLowerCase()}
                  </span>
                </p>
                {subscription && subscription.currentPeriodEnd && (
                  <p>
                    {t("common:subscription.nextBilling")}:{" "}
                    {new Date(
                      subscription.currentPeriodEnd,
                    ).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>

            <div className="subscription-features">
              <h5>{t("common:subscription.premiumBenefits")}</h5>
              <ul>
                <li>{t("common:subscription.features.unlimitedSearches")}</li>
                <li>{t("common:subscription.features.aiMatching")}</li>
                <li>{t("common:subscription.features.emailAutomation")}</li>
                <li>{t("common:subscription.features.prioritySupport")}</li>
              </ul>
            </div>

            <button
              className="btn-primary"
              onClick={handleManageSubscription}
              disabled={isCreatingPortal}
            >
              {isCreatingPortal ? (
                <>
                  <Loader2 size={16} className="loading-spinner" />
                  {t("common:subscription.openingPortal")}
                </>
              ) : (
                <>
                  <ExternalLink size={16} />
                  {t("common:subscription.manageSubscription")}
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="subscription-inactive">
            <div className="subscription-cta">
              <h4>{t("common:subscription.upgradeToPremium")}</h4>
              <p>{t("common:subscription.unlockFeatures")}</p>

              <div className="premium-features">
                <div className="feature-item">
                  <Crown size={16} className="feature-icon" />
                  <span>{t("common:subscription.unlimitedAccess")}</span>
                </div>
                <div className="feature-item">
                  <CreditCard size={16} className="feature-icon" />
                  <span>{t("common:subscription.cancelAnytime")}</span>
                </div>
              </div>
            </div>

            <button
              className="btn-primary btn-subscribe"
              onClick={handleSubscribe}
              disabled={isCreatingCheckout}
            >
              {isCreatingCheckout ? (
                <>
                  <Loader2 size={16} className="loading-spinner" />
                  {t("common:subscription.redirecting")}
                </>
              ) : (
                <>
                  <CreditCard size={16} />
                  {t("common:subscription.subscribeNow")}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSubscription;
